import React, { useMemo, useState } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../utils/cn';
import { Checkbox } from '../Checkbox/Checkbox';
import { Pagination } from '../Pagination/Pagination';
import { Skeleton } from '../Skeleton/Skeleton';
import { EmptyState } from '../EmptyState/EmptyState';

/**
 * DataTable — composé tri / sélection / pagination.
 *
 * Variantes :
 *   - default : header bg-bg-2 (classique)
 *   - duotone : header bg-ink text-cream + pagination footer bg-ink (piste 8)
 *
 * Options :
 *   - showRowIndex : ajoute une colonne 01/02/… mono en première position
 *   - data, columns, rowKey, isLoading, isSelectable, ...
 */

const VARIANTS = {
  default: {
    wrapper: 'border border-border rounded-lg bg-surface',
    thead:   'bg-bg-2/60 border-b border-border',
    th:      'text-mute',
    rowAlt:  'odd:bg-bg-2/40',
    rowDiv:  'divide-y divide-border',
  },
  duotone: {
    wrapper: 'rounded-lg overflow-hidden border border-ink/12',
    thead:   'bg-ink text-cream',
    th:      'text-cream',
    rowAlt:  'odd:bg-cream-2/40',
    rowDiv:  'divide-y divide-ink/8',
  },
};

export function DataTable({
  data = [],
  columns = [],
  rowKey = 'id',
  isLoading = false,
  isSelectable = false,
  selectedIds = [],
  onSelectionChange,
  page,
  pageSize,
  totalItems,
  onPageChange,
  onRowClick,
  onSort,                 // (key, direction)
  emptyState,
  className,
  size = 'md',            // 'sm' | 'md' | 'lg'
  variant = 'default',    // 'default' | 'duotone'
  showRowIndex = false,   // numérotation 01, 02, ...
  rowIndexStart = 1,
}) {
  const [localSort, setLocalSort] = useState({ key: null, dir: 'asc' });
  const v = VARIANTS[variant] || VARIANTS.default;

  const sortedRows = useMemo(() => {
    if (onSort || !localSort.key) return data;
    const col = columns.find((c) => c.key === localSort.key);
    if (!col) return data;
    const accessor = col.accessor || ((r) => r[col.key]);
    const sorted = [...data].sort((a, b) => {
      const av = accessor(a);
      const bv = accessor(b);
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av < bv) return localSort.dir === 'asc' ? -1 : 1;
      if (av > bv) return localSort.dir === 'asc' ?  1 : -1;
      return 0;
    });
    return sorted;
  }, [data, localSort, columns, onSort]);

  const totalPages = pageSize && totalItems ? Math.ceil(totalItems / pageSize) : 1;
  const allSelected = sortedRows.length > 0 && sortedRows.every((r) => selectedIds.includes(r[rowKey]));
  const someSelected = !allSelected && sortedRows.some((r) => selectedIds.includes(r[rowKey]));

  const toggleAll = () => {
    if (allSelected) {
      onSelectionChange?.(selectedIds.filter((id) => !sortedRows.find((r) => r[rowKey] === id)));
    } else {
      const ids = sortedRows.map((r) => r[rowKey]);
      onSelectionChange?.([...new Set([...selectedIds, ...ids])]);
    }
  };
  const toggleRow = (id) => {
    if (selectedIds.includes(id)) onSelectionChange?.(selectedIds.filter((x) => x !== id));
    else onSelectionChange?.([...selectedIds, id]);
  };

  const handleHeaderClick = (col) => {
    if (!col.sortable) return;
    const isCurrent = (onSort ? col.key === localSort.key : localSort.key === col.key);
    const nextDir = isCurrent && localSort.dir === 'asc' ? 'desc' : 'asc';
    setLocalSort({ key: col.key, dir: nextDir });
    onSort?.(col.key, nextDir);
  };

  const sizeRow = { sm: 'py-2', md: 'py-3', lg: 'py-3.5' }[size];
  const sizeHead = { sm: 'py-2', md: 'py-2.5', lg: 'py-3' }[size];
  const extraColCount = (isSelectable ? 1 : 0) + (showRowIndex ? 1 : 0);

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <div className={cn('overflow-x-auto', v.wrapper)}>
        <table className="w-full text-sm border-collapse">
          <thead className={v.thead}>
            <tr>
              {isSelectable && (
                <th className={cn('w-10 px-3 text-left align-middle', sizeHead)}>
                  <Checkbox
                    aria-label="Sélectionner tout"
                    isIndeterminate={someSelected}
                    checked={allSelected}
                    onChange={toggleAll}
                  />
                </th>
              )}
              {showRowIndex && (
                <th
                  scope="col"
                  className={cn(
                    'w-10 px-3 text-left align-middle text-[10.5px] uppercase tracking-wider font-semibold',
                    sizeHead,
                    v.th,
                  )}
                >
                  №
                </th>
              )}
              {columns.map((col) => {
                const isSorted = localSort.key === col.key;
                const Icon = isSorted ? (localSort.dir === 'asc' ? ChevronUp : ChevronDown) : ChevronsUpDown;
                return (
                  <th
                    key={col.key}
                    scope="col"
                    style={col.width ? { width: col.width } : undefined}
                    className={cn(
                      'text-left px-3 text-[10.5px] uppercase tracking-wider font-semibold align-middle',
                      sizeHead,
                      v.th,
                      col.align === 'right' && 'text-right',
                      col.align === 'center' && 'text-center',
                      col.sortable && 'cursor-pointer select-none hover:opacity-90',
                    )}
                    onClick={col.sortable ? () => handleHeaderClick(col) : undefined}
                    aria-sort={isSorted ? (localSort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                  >
                    <span className={cn('inline-flex items-center gap-1', col.align === 'right' && 'flex-row-reverse')}>
                      {col.header}
                      {col.sortable && <Icon className={cn('w-3 h-3', !isSorted && 'opacity-40')} strokeWidth={2} />}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className={v.rowDiv}>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={`sk-${i}`}>
                  {isSelectable && <td className={cn('px-3', sizeRow)}><Skeleton variant="rect" className="w-4 h-4" /></td>}
                  {showRowIndex && <td className={cn('px-3', sizeRow)}><Skeleton variant="text" className="h-3 w-5" /></td>}
                  {columns.map((col) => (
                    <td key={col.key} className={cn('px-3', sizeRow)}><Skeleton variant="text" className="h-3.5" /></td>
                  ))}
                </tr>
              ))
            ) : sortedRows.length === 0 ? (
              <tr>
                <td colSpan={columns.length + extraColCount} className="p-0">
                  {emptyState || <EmptyState title="Aucun résultat" description="Aucune ligne à afficher pour le moment." />}
                </td>
              </tr>
            ) : (
              sortedRows.map((row, idx) => {
                const id = row[rowKey];
                const isSel = selectedIds.includes(id);
                return (
                  <tr
                    key={id}
                    className={cn(
                      'transition-colors',
                      isSel ? 'bg-bg-2/60' : variant === 'duotone' ? 'hover:bg-cream-2/40' : 'hover:bg-bg-2/50',
                      onRowClick && 'cursor-pointer',
                    )}
                    onClick={(e) => {
                      if (e.target.closest('button, a, input, label, [data-no-row-click]')) return;
                      onRowClick?.(row);
                    }}
                  >
                    {isSelectable && (
                      <td className={cn('px-3 align-middle', sizeRow)} data-no-row-click>
                        <Checkbox
                          aria-label="Sélectionner la ligne"
                          checked={isSel}
                          onChange={() => toggleRow(id)}
                        />
                      </td>
                    )}
                    {showRowIndex && (
                      <td className={cn('px-3 align-middle font-mono text-[11.5px] text-mute', sizeRow)}>
                        {String(rowIndexStart + idx).padStart(2, '0')}
                      </td>
                    )}
                    {columns.map((col) => (
                      <td
                        key={col.key}
                        className={cn(
                          'px-3 align-middle',
                          sizeRow,
                          col.align === 'right' && 'text-right',
                          col.align === 'center' && 'text-center',
                          col.cellClassName,
                        )}
                      >
                        {col.render ? col.render(row) : (col.accessor ? col.accessor(row) : row[col.key])}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {/* Pagination intégrée duotone (footer bg-ink) */}
        {variant === 'duotone' && pageSize && totalItems > 0 && totalPages > 1 && (
          <div className="bg-ink text-cream/80 px-4 py-2.5 flex items-center justify-between text-[12px]">
            <span className="font-mono">
              {Math.min((page - 1) * pageSize + 1, totalItems)} — {Math.min(page * pageSize, totalItems)} / {totalItems}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => page > 1 && onPageChange?.(page - 1)}
                disabled={page === 1}
                aria-label="Précédent"
                className="h-7 w-7 rounded hover:bg-ink-2 inline-flex items-center justify-center disabled:opacity-40 disabled:pointer-events-none"
              >
                <ChevronLeft className="w-3.5 h-3.5" strokeWidth={2} />
              </button>
              <span className="font-mono">{page} / {totalPages}</span>
              <button
                type="button"
                onClick={() => page < totalPages && onPageChange?.(page + 1)}
                disabled={page === totalPages}
                aria-label="Suivant"
                className="h-7 w-7 rounded hover:bg-ink-2 inline-flex items-center justify-center disabled:opacity-40 disabled:pointer-events-none"
              >
                <ChevronRight className="w-3.5 h-3.5" strokeWidth={2} />
              </button>
            </div>
          </div>
        )}
      </div>

      {variant !== 'duotone' && pageSize && totalItems > 0 && totalPages > 1 && (
        <Pagination
          page={page}
          total={totalPages}
          onChange={onPageChange}
          totalItems={totalItems}
          pageSize={pageSize}
        />
      )}

      {isSelectable && selectedIds.length > 0 && (
        <div className="text-xs text-mute">
          <span className="text-text font-medium">{selectedIds.length}</span> ligne(s) sélectionnée(s)
        </div>
      )}
    </div>
  );
}

export default DataTable;
